import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  AgreementGrid,
  type AgreementPage,
  type Folding,
  type ReviewActions,
} from '../components/agreements/AgreementGrid.js'
import { AgreementBuilder, type AgreementCreated } from '../components/agreements/AgreementBuilder.js'
import { AgreementBulkBar } from '../components/agreements/AgreementBulkBar.js'
import { AgreementRail } from '../components/agreements/AgreementRail.js'
import { AgreementTable, type SavePatch } from '../components/agreements/AgreementTable.js'
import {
  groupRows,
  isShow,
  narrowed,
  SHOWS,
  shows,
  type AgreementGroup,
  type GroupBy,
  type Look,
  type Narrowing,
  type Show,
} from '../components/agreements/agreement-view.js'
import { ComingUp, linesShown, type WorkLine } from '../components/agreements/ComingUp.js'
import {
  AddSitesPopup,
  AgreementPopup,
  ServicePopup,
  SitePopup,
  useInvalidateAgreement,
} from '../components/agreements/AgreementPopups.js'
import { TickBox, tickedOf } from '../components/agreements/review-parts.js'
import { VisitPopup } from '../components/agreements/VisitPopup.js'
import { Icon } from '../components/Icon.js'
import { dayLabel, leadLabel } from '../components/repeat/rule.js'
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  FilterChip,
  Page,
  PageHeader,
  SearchBox,
  Segmented,
  Select,
  SkeletonRows,
  StatStrip,
  StatTile,
} from '../components/ui.js'
import { cn } from '../lib/cn.js'
import { addressLine, wholeDollars } from '../lib/format.js'
import { useSession } from '../lib/session.js'
import { errorMessage, useTRPC } from '../lib/trpc.js'
import { AGREEMENT_STATUS, termLabel } from './Agreements.js'

/**
 * More groups or services than these and the page opens folded, a line a
 * group, until it is narrowed: 168 sites, or three services across 351.
 */
const FOLD_GROUPS_ABOVE = 8
const FOLD_ROWS_ABOVE = 40

/**
 * One service agreement, laid out to be worked through. Decision 96, ADR 027
 * and ADR 028.
 *
 * The header says which agreement; the figures under it say what it is worth
 * and what it asks for — visits due in eight weeks, jobs to make, visits
 * overdue, services with no price — and each of those four is a filter. The
 * rail beside the list holds the term, how jobs are made, the notes and the
 * key, so they are in view however long the agreement is.
 *
 * The list is read by site or by service, as a timeline of visits, a table of
 * figures or Coming up, the next twelve weeks as one line a job with Make
 * job; narrowed by Show, a service and a search. All of that lives in the
 * URL. A long agreement folds to a line a group until it is narrowed. Adding
 * is on the page: Add site in the header and under the grid, Add service on
 * each site, Add sites on each service. Ticked services are changed together
 * from the bar at the foot.
 */
export function AgreementDetail() {
  const { id = '' } = useParams()
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const location = useLocation()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const { can } = useSession()
  const canEdit = can('CLIENT_EDIT')
  const invalidate = useInvalidateAgreement()

  const by: GroupBy = params.get('by') === 'service' ? 'service' : 'site'
  const lookParam = params.get('look')
  const look: Look = lookParam === 'table' || lookParam === 'coming' ? lookParam : 'timeline'
  const showParam = params.get('show')
  const narrowing: Narrowing = {
    show: isShow(showParam) ? showParam : 'all',
    service: params.get('service') || null,
    term: params.get('q') ?? '',
  }
  const { show, service, term } = narrowing
  const setParam = (key: string, value: string, fallback: string) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current)
        if (value === fallback) next.delete(key)
        else next.set(key, value)
        return next
      },
      { replace: true, state: location.state },
    )
  const setShow = (next: Show) => setParam('show', show === next ? 'all' : next, 'all')

  const [adding, setAdding] = useState<{ siteIds: string[] } | null>(null)
  const [addingSites, setAddingSites] = useState<AgreementGroup | null>(null)
  const [editing, setEditing] = useState(false)
  const [editingService, setEditingService] = useState<string | null>(null)
  const [editingSite, setEditingSite] = useState<string | null>(null)
  const [openVisit, setOpenVisit] = useState<{ scheduleId: string; sequence: number } | null>(null)
  const [ticked, setTicked] = useState<ReadonlySet<string>>(new Set())
  const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null)
  const [making, setMaking] = useState<ReadonlySet<string>>(new Set())
  // Which groups are open, for the view it was set in: a new grouping,
  // chip or search starts again from that view's default.
  const view = [by, show, service ?? '', term].join('|')
  const [folding, setFolding] = useState<{ view: string; all: boolean | null; flipped: ReadonlySet<string> }>({
    view,
    all: null,
    flipped: new Set(),
  })
  const created = (location.state as { created?: AgreementCreated } | null)?.created ?? null

  const { data: page, isPending, error, refetch } = useQuery(trpc.agreements.byId.queryOptions({ id }))
  const setStatus = useMutation(
    trpc.agreements.update.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries(trpc.agreements.pathFilter())
        void queryClient.invalidateQueries(trpc.repeat.pathFilter())
      },
    }),
  )
  const patch = useMutation(trpc.agreements.patchServices.mutationOptions({ onSuccess: invalidate }))
  const removeMany = useMutation(trpc.agreements.removeServices.mutationOptions({ onSuccess: invalidate }))
  const makeJob = useMutation(trpc.repeat.createJob.mutationOptions())
  const rename = useMutation(
    trpc.clients.updateSite.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries(trpc.clients.pathFilter())
        void queryClient.invalidateQueries(trpc.jobs.pathFilter())
        invalidate()
      },
    }),
  )

  const groups = useMemo(() => (page ? groupRows(page, by, narrowing) : []), [page, by, show, service, term])

  if (error) return <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
  if (isPending) return <SkeletonRows />

  const status = AGREEMENT_STATUS[page.status]
  const ended = page.status === 'ENDED'
  const paused = page.status === 'PAUSED'
  const editable = canEdit && !ended
  const addSite = editable ? () => setAdding({ siteIds: [] }) : undefined
  const addService = editable ? (siteId: string) => setAdding({ siteIds: [siteId] }) : undefined
  const serviceRow = page.schedules.find((row) => row.id === editingService) ?? null

  // A service removed elsewhere drops out of the ticks by itself.
  const tickedRows = page.schedules.filter((row) => ticked.has(row.id))
  const shownIds = groups.flatMap((group) => group.rows.map((row) => row.id))
  const lines = linesShown(page.upcoming, new Set(shownIds))
  const select = (ids: readonly string[], on: boolean) =>
    setTicked((current) => {
      const next = new Set(current)
      for (const scheduleId of ids) {
        if (on) next.add(scheduleId)
        else next.delete(scheduleId)
      }
      return next
    })
  const failed = (cause: unknown) => {
    setNotice({ tone: 'error', text: `That did not save: ${errorMessage(cause)}` })
    throw cause
  }
  const save: SavePatch = (scheduleIds, change) =>
    patch.mutateAsync({ agreementId: page.id, scheduleIds: [...scheduleIds], ...change }).catch(failed)
  const actions: ReviewActions = {
    selected: editable ? ticked : undefined,
    onSelect: editable ? select : undefined,
    onAddService: addService,
    onAddSites: editable ? setAddingSites : undefined,
    onAddSite: addSite,
    onEditSite: editable ? setEditingSite : undefined,
    onEditService: editable ? (row) => setEditingService(row.id) : undefined,
    onRenameSite: editable ? (siteId, name) => rename.mutateAsync({ id: siteId, name }).catch(failed) : undefined,
    onVisit: setOpenVisit,
  }

  // Each line is its own job and its own transaction, so one that can no
  // longer be made does not stop the rest.
  const make =
    can('SCHEDULE_EDIT') && page.status === 'ACTIVE'
      ? async (work: readonly WorkLine[]) => {
          setMaking(new Set(work.map((line) => line.key)))
          let made = 0
          let refused: string | null = null
          for (const line of work) {
            try {
              await makeJob.mutateAsync({ scheduleId: line.scheduleId, sequence: line.sequence, with: line.with })
              made += 1
            } catch (cause) {
              refused ??= errorMessage(cause)
            }
          }
          setMaking(new Set())
          invalidate()
          void queryClient.invalidateQueries(trpc.jobs.pathFilter())
          const jobs = (n: number) => `${n} job${n === 1 ? '' : 's'}`
          setNotice(
            refused === null
              ? { tone: 'ok', text: `Made ${jobs(made)}.` }
              : { tone: 'error', text: `Made ${made} of ${jobs(work.length)}. ${refused}` },
          )
        }
      : undefined
  const readyTicked = page.upcoming.filter((line) => line.ready && line.scheduleIds.some((scheduleId) => ticked.has(scheduleId)))

  const folds = groups.length > 1
  const foldedByDefault =
    !narrowed(narrowing) && (groups.length > FOLD_GROUPS_ABOVE || shownIds.length > FOLD_ROWS_ABOVE)
  const current = folding.view === view ? folding : { view, all: null, flipped: new Set<string>() }
  const fold: Folding | undefined = folds
    ? {
        isOpen: (key) => (current.all ?? !foldedByDefault) !== current.flipped.has(key),
        toggle: (key) => {
          const flipped = new Set(current.flipped)
          if (!flipped.delete(key)) flipped.add(key)
          setFolding({ ...current, flipped })
        },
      }
    : undefined

  const serviceGroups = new Set(
    page.schedules.map((row) => row.services.map((entry) => entry.serviceTypeId).sort().join('+')),
  ).size
  const serviceOptions = [
    ...new Map(page.schedules.flatMap((row) => row.services.map((entry) => [entry.serviceTypeId, entry.name] as const))),
  ].sort((a, b) => a[1].localeCompare(b[1]))
  const counted = Object.fromEntries(
    SHOWS.map((entry) => [entry.value, page.schedules.filter((row) => shows(row, entry.value)).length]),
  ) as Record<Show, number>
  const dueSoon = page.schedules.reduce((sum, row) => sum + row.dueSoon, 0)
  const overdue = page.schedules.reduce((sum, row) => sum + row.overdue, 0)
  const jobsToMake = page.upcoming.filter((line) => line.ready).length
  const plural = (n: number, word: string) => `${n.toLocaleString('en-AU')} ${word}${n === 1 ? '' : 's'}`

  const siteRows = page.schedules.filter((row) => row.siteId === editingSite)
  const site = editingSite
    ? (page.clientSites.find((candidate) => candidate.id === editingSite) ?? siteRows[0]?.site ?? null)
    : null

  return (
    <Page width="xwide">
      <PageHeader
        eyebrow={
          <Link to="/agreements" className="inline-flex items-center gap-1 hover:text-white">
            <Icon name="back" className="size-3.5" />
            Service agreements
            <span className="text-sky/60">{`· AG-${page.number}`}</span>
          </Link>
        }
        title={page.name}
        description={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            {/* The shell's header carries the page name on a phone. */}
            <span className="font-semibold text-ink-900 desk:hidden">{page.name}</span>
            <Badge tone={status.tone}>{status.label}</Badge>
            <Link
              to={`/clients/${page.client.id}`}
              className="font-medium text-ink-800 hover:underline desk:font-semibold desk:text-white"
            >
              {page.client.name}
            </Link>
            {page.contact ? (
              <Dotted>{[page.contact.name, page.contact.position].filter(Boolean).join(' · ')}</Dotted>
            ) : null}
            <Dotted>{termLabel(page)}</Dotted>
            <Dotted>{`${plural(page.sites, 'site')} · ${plural(page.services, 'service')}`}</Dotted>
          </span>
        }
        actions={
          canEdit ? (
            <>
              <Button icon={<Icon name="edit" className="size-4" />} onClick={() => setEditing(true)}>
                Edit
              </Button>
              {!ended ? (
                <Button
                  icon={<Icon name={paused ? 'play' : 'pause'} className="size-3.5" />}
                  loading={setStatus.isPending}
                  onClick={() => setStatus.mutate({ id: page.id, status: paused ? 'ACTIVE' : 'PAUSED' })}
                >
                  {paused ? 'Resume' : 'Pause'}
                </Button>
              ) : null}
              {addSite ? (
                <Button variant="primary" icon={<Icon name="plus" className="size-4" />} onClick={addSite}>
                  Add site
                </Button>
              ) : null}
            </>
          ) : undefined
        }
      />

      {created ? (
        <div role="status" className="flex items-center gap-2.5 rounded-lg bg-green-50 px-4 py-2.5 text-[13px] text-green-800 ring-1 ring-green-200 ring-inset">
          <Icon name="check" className="size-4 shrink-0 text-green-600" strokeWidth={2.5} />
          <span className="min-w-0 flex-1">{createdLine(created)}</span>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => void navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: null })}
            className="rounded p-0.5 text-green-700 hover:bg-green-100"
          >
            <Icon name="close" className="size-3.5" />
          </button>
        </div>
      ) : null}

      {setStatus.error ? <ErrorState message={errorMessage(setStatus.error)} /> : null}

      <StatStrip>
        <StatTile label="Value" value={`${wholeDollars(page.aYear)} a year`} caption={`${plural(page.visitsInTerm, 'visit')} this term`} />
        <StatTile
          label="Next visit"
          value={ended ? '—' : paused ? 'Paused' : page.nextVisit ? dayLabel(page.nextVisit.on) : '—'}
          caption={nextSub(page)}
        />
        <StatTile
          label="Due in 8 weeks"
          value={dueSoon}
          caption={`${dueSoon === 1 ? 'visit' : 'visits'} on ${plural(counted.soon, 'service')}`}
          active={show === 'soon'}
          onClick={() => setShow('soon')}
        />
        <StatTile
          label="Needs a job"
          value={jobsToMake}
          caption={paused ? 'Not while paused' : `Due within ${leadLabel(page.leadDays)}, no job yet`}
          active={show === 'job'}
          onClick={() => setShow('job')}
        />
        <StatTile
          label="Overdue"
          value={overdue}
          tone={overdue > 0 ? 'red' : undefined}
          caption={overdue > 0 ? 'Due, and no job' : 'Nothing late'}
          active={show === 'overdue'}
          onClick={() => setShow('overdue')}
        />
        <StatTile
          label="No price"
          value={counted.noprice}
          tone={counted.noprice > 0 ? 'amber' : undefined}
          caption={counted.noprice > 0 ? 'Worth $0 until priced' : 'Every service priced'}
          active={show === 'noprice'}
          onClick={() => setShow('noprice')}
        />
      </StatStrip>

      <div className="grid items-start gap-[var(--stack-gap)] xl:grid-cols-[minmax(0,1fr)_280px]">
        <Card className="min-w-0">
          {page.schedules.length === 0 ? (
            <EmptyState
              title="Nothing in this agreement yet"
              description="Add a site and the services done there. Each one repeats on its own schedule."
              action={
                addSite ? (
                  <Button variant="primary" icon={<Icon name="plus" className="size-4" />} onClick={addSite}>
                    Add site
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2 border-b border-ink-100 px-[var(--card-pad)] py-2.5">
                {editable && shownIds.length > 0 ? (
                  <TickBox
                    checked={tickedOf(shownIds, ticked)}
                    onChange={(on) => select(shownIds, on)}
                    label={narrowed(narrowing) ? 'Select every service found' : 'Select every service'}
                    className="mr-1"
                  />
                ) : null}
                <Segmented<GroupBy>
                  label="Group the agreement"
                  value={by}
                  onChange={(value) => setParam('by', value, 'site')}
                  options={[
                    { value: 'site', label: 'By site', count: page.sites },
                    { value: 'service', label: 'By service', count: serviceGroups },
                  ]}
                />
                <Segmented<Look>
                  label="Layout"
                  value={look}
                  onChange={(value) => setParam('look', value, 'timeline')}
                  options={[
                    { value: 'timeline', label: 'Timeline' },
                    { value: 'table', label: 'Table' },
                    { value: 'coming', label: 'Coming up' },
                  ]}
                />
                <SearchBox
                  className="sm:ml-auto"
                  placeholder="Search sites or services"
                  value={term}
                  onChange={(value) => setParam('q', value, '')}
                />
              </div>

              <div className="flex flex-wrap items-center gap-1.5 border-b border-ink-100 bg-ink-50 px-[var(--card-pad)] py-2">
                <span className="mr-1 text-[11px] font-semibold tracking-wider text-ink-500 uppercase">Show</span>
                {SHOWS.map((entry) => (
                  <FilterChip
                    key={entry.value}
                    active={show === entry.value}
                    onClick={() => setParam('show', entry.value, 'all')}
                    className="gap-1"
                  >
                    {entry.label}
                    <span
                      className={cn(
                        'tabular-nums',
                        show === entry.value
                          ? 'text-white/70'
                          : counted[entry.value] > 0 && entry.tone === 'red'
                            ? 'text-red-700'
                            : counted[entry.value] > 0 && entry.tone === 'amber'
                              ? 'text-amber-700'
                              : 'text-ink-400',
                      )}
                    >
                      {counted[entry.value]}
                    </span>
                  </FilterChip>
                ))}
                {serviceOptions.length > 1 ? (
                  <Select
                    aria-label="Service"
                    value={service ?? ''}
                    onChange={(event) => setParam('service', event.target.value, '')}
                    className="ml-1 h-[var(--control-sm)] w-auto min-w-0 py-0 text-xs sm:max-w-56"
                  >
                    <option value="">All services</option>
                    {serviceOptions.map(([serviceTypeId, name]) => (
                      <option key={serviceTypeId} value={serviceTypeId}>
                        {name}
                      </option>
                    ))}
                  </Select>
                ) : null}
                {fold && look !== 'coming' ? (
                  <span className="ml-auto flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setFolding({ view, all: true, flipped: new Set() })}>
                      Open all
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setFolding({ view, all: false, flipped: new Set() })}>
                      Fold all
                    </Button>
                  </span>
                ) : null}
              </div>

              {notice ? (
                <div
                  role={notice.tone === 'error' ? 'alert' : 'status'}
                  className={
                    notice.tone === 'error'
                      ? 'flex items-center gap-2 border-b border-red-200 bg-red-50 px-[var(--card-pad)] py-2 text-[12.5px] text-red-800'
                      : 'flex items-center gap-2 border-b border-green-200 bg-green-50 px-[var(--card-pad)] py-2 text-[12.5px] text-green-800'
                  }
                >
                  <span className="min-w-0 flex-1">{notice.text}</span>
                  <button type="button" aria-label="Dismiss" onClick={() => setNotice(null)} className="rounded p-0.5 hover:bg-white/60">
                    <Icon name="close" className="size-3.5" />
                  </button>
                </div>
              ) : null}

              <div className="px-[var(--card-pad)] pt-3 pb-4">
                {groups.length === 0 ? (
                  <div className="flex flex-col items-center gap-2 py-6 text-center text-[13px] text-ink-500">
                    <p>{term ? `Nothing on this agreement matches “${term}”.` : 'Nothing on this agreement matches.'}</p>
                    {show !== 'all' || service ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          setParams(
                            (currentParams) => {
                              const next = new URLSearchParams(currentParams)
                              next.delete('show')
                              next.delete('service')
                              return next
                            },
                            { replace: true, state: location.state },
                          )
                        }
                      >
                        Show every service
                      </Button>
                    ) : null}
                  </div>
                ) : look === 'coming' ? (
                  <ComingUp page={page} lines={lines} actions={actions} onMake={make} making={making} />
                ) : look === 'table' ? (
                  <AgreementTable page={page} groups={groups} by={by} actions={actions} onSave={editable ? save : undefined} fold={fold} />
                ) : (
                  <AgreementGrid page={page} groups={groups} by={by} actions={actions} fold={fold} />
                )}
              </div>
            </>
          )}
        </Card>

        <AgreementRail
          page={page}
          term={termLabel(page)}
          termSub={termSub(page)}
          onEdit={canEdit ? () => setEditing(true) : undefined}
        />
      </div>

      {editable && tickedRows.length > 0 ? (
        <AgreementBulkBar
          rows={tickedRows}
          term={{ min: page.startsOn, max: page.termEndsOn }}
          onPatch={(change) => save(tickedRows.map((row) => row.id), change)}
          onRemove={() =>
            removeMany
              .mutateAsync({ agreementId: page.id, scheduleIds: tickedRows.map((row) => row.id) })
              .then(() => setTicked(new Set()))
              .catch(failed)
          }
          onClear={() => setTicked(new Set())}
          makeJobs={
            make && look === 'coming'
              ? {
                  count: readyTicked.length,
                  busy: readyTicked.some((line) => making.has(line.key)),
                  onMake: () => void make(readyTicked),
                }
              : undefined
          }
        />
      ) : null}

      {adding ? (
        <AgreementBuilder
          open
          mode={{ kind: 'extend', agreementId: page.id, siteIds: adding.siteIds }}
          onClose={() => setAdding(null)}
        />
      ) : null}
      <AgreementPopup open={editing} onClose={() => setEditing(false)} agreement={page} />
      <AddSitesPopup
        page={page}
        group={addingSites}
        onClose={() => setAddingSites(null)}
        onAdded={(text) => setNotice({ tone: 'ok', text })}
      />
      <ServicePopup
        agreementId={page.id}
        row={serviceRow}
        term={{ startsOn: page.startsOn, endsOn: page.termEndsOn }}
        onClose={() => setEditingService(null)}
      />
      <VisitPopup
        page={page}
        visit={openVisit}
        canSchedule={can('SCHEDULE_EDIT') && page.status === 'ACTIVE'}
        onClose={() => setOpenVisit(null)}
        onMade={(text) => setNotice({ tone: 'ok', text })}
      />
      <SitePopup
        agreementId={page.id}
        site={site}
        services={siteRows.length}
        paused={siteRows.length > 0 && siteRows.every((row) => row.stoppedAt !== null)}
        onClose={() => setEditingSite(null)}
      />
    </Page>
  )
}

/** A part of the header's line, after a dot. */
function Dotted({ children }: { children: string }) {
  return (
    <>
      <span aria-hidden="true" className="text-ink-300 desk:text-white/30">
        •
      </span>
      <span>{children}</span>
    </>
  )
}

function createdLine(created: AgreementCreated): string {
  const lead = leadLabel(created.leadDays)
  if (!created.autoCreate) {
    return `Agreement created. Automatic creation is off, so each visit’s job is made by hand from Coming up.`
  }
  if (created.made === 0) return `Agreement created. Each visit’s job is created ${lead} before it’s due.`
  return `Agreement created. ${created.made} job${created.made === 1 ? ' is' : 's are'} already on the schedule; the rest are created ${lead} before they’re due.`
}

function termSub(page: AgreementPage): string {
  if (page.status === 'ENDED') return 'Ended'
  if (!page.endsOn) return 'No end date'
  return page.autoRenew ? 'Renews automatically' : 'Stops at term end'
}

function nextSub(page: AgreementPage): string | undefined {
  if (page.status === 'ENDED') return undefined
  if (page.status === 'PAUSED') return 'No new jobs are made'
  const next = page.nextVisit
  if (!next) return undefined
  const site = next.site.name ?? addressLine(next.site.address)
  return [next.service, site].filter(Boolean).join(' · ')
}

